import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { BILLS_PRODUCT_GROUP_TYPE_CODE } from '@/domains/BillPaymentDomain'
import { BILL_PAYMENT_ROUTES } from '@/domains/CommonDomain'
import { IRootState } from '@/modules'
import { billPaymentAmountActions } from '@/modules/bill-payment/BillPaymentAmountModule'
import { push } from '@/utils/historyUtils'
import { isEmpty, isOrUnderLength, isValidRange } from '@/utils/validationUtils'

export const BILL_PAYMENT_PRODUCT = 'BillPaymentProduct'

const SUBMIT = `${BILL_PAYMENT_PRODUCT}/submit`
const INPUT_CUSTOMER_ID = `${BILL_PAYMENT_PRODUCT}/inputCustomerId`
const SET_VIEW = `${BILL_PAYMENT_PRODUCT}/setView`
const CLEAR_VIEW = `${BILL_PAYMENT_PRODUCT}/clearView`

const CUSTOMER_ID_MIN_LENGTH = 1
const CUSTOMER_ID_MAX_LENGTH = 20
const PHONE_NUMBER_PREFIX = '08'
const PHONE_NUMBER_MIN_LENGTH = 10
const PHONE_NUMBER_MAX_LENGTH = 15

interface IBillPaymentProductSubmitAction {
  productGroupTypeCode: string
}

export interface IBillPaymentProductView {
  visiblePaymentLayer: boolean
  customerId: string
  selectedProductTypeCode: string
  selectedProductTypeName: string
  invalidCustomerId: boolean
}

export interface IBillPaymentProductState {
  isValid: boolean
  view: IBillPaymentProductView
}

export const initialState: IBillPaymentProductState = {
  // TODO validation 방법 고민
  isValid: false,
  view: {
    visiblePaymentLayer: false,
    customerId: '',
    selectedProductTypeCode: '',
    selectedProductTypeName: '',
    invalidCustomerId: false,
  },
}

export const billPaymentProductActions = {
  setView: createAction<Partial<IBillPaymentProductView>>(SET_VIEW),
  inputCustomerId: createAction<string>(INPUT_CUSTOMER_ID),
  submit: createAction<IBillPaymentProductSubmitAction>(SUBMIT),
  clearView: createAction(CLEAR_VIEW),
}

const reducer = {
  [SET_VIEW]: (
    state: IBillPaymentProductState,
    action: Action<Partial<IBillPaymentProductView>>,
  ) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CLEAR_VIEW]: (state: IBillPaymentProductState) => ({
    ...state,
    view: initialState.view,
  }),
}

export const billPaymentProductReducer = handleActions(reducer, initialState)

export const billPaymentProductSelector = {
  isValid: (state: IRootState) => state[BILL_PAYMENT_PRODUCT].isValid,
  view: (state: IRootState) => state[BILL_PAYMENT_PRODUCT].view ?? initialState.view,
  isNumberKeyEnabled: (state: IRootState, productGroupTypeCode: string): boolean => {
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view
    const isPln = productGroupTypeCode === BILLS_PRODUCT_GROUP_TYPE_CODE.PLN

    if (isPln) {
      return isOrUnderLength(customerId, CUSTOMER_ID_MAX_LENGTH - 1)
    }

    return isOrUnderLength(customerId, PHONE_NUMBER_MAX_LENGTH - 1)
  },
  isValidPlnCustomerId: (state: IRootState): boolean => {
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view

    return isValidRange(CUSTOMER_ID_MIN_LENGTH, CUSTOMER_ID_MAX_LENGTH)(customerId.length)
  },
  isValidPhoneNumber: (state: IRootState): boolean => {
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view
    if (!customerId.startsWith(PHONE_NUMBER_PREFIX)) {
      return false
    }

    return isValidRange(PHONE_NUMBER_MIN_LENGTH, PHONE_NUMBER_MAX_LENGTH)(customerId.length)
  },
  isValidCustomerId: (state: IRootState, productGroupTypeCode: string): boolean => {
    const isPln = productGroupTypeCode === BILLS_PRODUCT_GROUP_TYPE_CODE.PLN

    if (isPln) {
      return billPaymentProductSelector.isValidPlnCustomerId(state)
    }

    return billPaymentProductSelector.isValidPhoneNumber(state)
  },
  isValidCustomerIdSize: (state: IRootState, productGroupTypeCode: string): boolean => {
    const isPln = productGroupTypeCode === BILLS_PRODUCT_GROUP_TYPE_CODE.PLN
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view

    if (isPln) {
      return isValidRange(CUSTOMER_ID_MIN_LENGTH, CUSTOMER_ID_MAX_LENGTH)(customerId.length)
    }

    return isValidRange(PHONE_NUMBER_MIN_LENGTH, PHONE_NUMBER_MAX_LENGTH)(customerId.length)
  },
}

function* inputCustomerId(action: Action<string>) {
  const customerId = action.payload

  if (isEmpty(customerId)) {
    yield put(billPaymentProductActions.setView({ invalidCustomerId: false }))
  }

  yield put(
    billPaymentProductActions.setView({
      customerId,
    }),
  )
}

function* submit(action: Action<IBillPaymentProductSubmitAction>) {
  const { productGroupTypeCode } = action.payload

  const isValidCustomerId = yield select(
    billPaymentProductSelector.isValidCustomerId,
    productGroupTypeCode,
  )
  if (!isValidCustomerId) {
    return yield put(billPaymentProductActions.setView({ invalidCustomerId: true }))
  }

  const {
    customerId,
    selectedProductTypeCode: productTypeCode,
  }: IBillPaymentProductView = yield select(billPaymentProductSelector.view)

  yield put(billPaymentAmountActions.setCustomerId(customerId))
  yield call(push, `${BILL_PAYMENT_ROUTES.AMOUNT}/${productTypeCode}`)
}

export const billPaymentProductSaga = [
  takeLatest(INPUT_CUSTOMER_ID, inputCustomerId),
  takeLatest(SUBMIT, submit),
]
